import { useEffect, useRef } from 'react';
import { Button } from '@predoc/cow';
import { ArrowUpToLine } from 'lucide-react';
import { PDFViewer } from '@/components';
import type { DocumentViewerPatient } from '../documents/viewer/types';
import { ReferenceLoadingIndicator } from './ReferenceLoadingIndicator';
import type { AnchorScrollTarget, FacesheetSourceContent } from './types';

interface FacesheetDocumentBodyProps {
  source: FacesheetSourceContent;
  patient?: DocumentViewerPatient;
  targetPage?: number;
  onPageChange?: (page: number) => void;
  anchorScrollTarget?: AnchorScrollTarget;
  anchorScrollKey?: number;
  isLoading?: boolean;
}

const scrollToAnchor = (container: HTMLDivElement, anchorId: string) => {
  const element = container.querySelector<HTMLElement>(
    `#${CSS.escape(anchorId)}`,
  );
  if (!element) return;

  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  element.focus({ preventScroll: true });
};

export const FacesheetDocumentBody = ({
  source,
  patient,
  targetPage,
  onPageChange,
  anchorScrollTarget,
  anchorScrollKey = 0,
  isLoading = false,
}: FacesheetDocumentBodyProps) => {
  const templateScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchorScrollTarget) return;

    const container = templateScrollRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      resizeObserver.disconnect();
      scrollToAnchor(container, anchorScrollTarget.anchorId);
    });

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [anchorScrollTarget, anchorScrollKey]);

  if (source.kind === 'pdf') {
    return (
      <div className="relative h-full flex-1 overflow-hidden bg-neutral-200">
        {isLoading ? (
          <ReferenceLoadingIndicator className="absolute inset-0 z-20 bg-neutral-200/80" />
        ) : null}
        <PDFViewer
          file={source.signedUrl}
          className="h-full"
          targetPage={targetPage}
          onPageChange={onPageChange}
          backgroundClassName="bg-neutral-200"
        />
      </div>
    );
  }

  const handleScrollToTop = () => {
    templateScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative h-full flex-1 overflow-hidden bg-neutral-200">
      {isLoading ? (
        <ReferenceLoadingIndicator className="absolute inset-0 z-20 bg-neutral-200/80" />
      ) : null}
      <div
        ref={templateScrollRef}
        className="h-full overflow-auto px-9 py-8"
        role="document"
        aria-label="Reference document"
      >
        {source.render({ patient })}
      </div>
      <Button
        variant="default"
        size="icon"
        onClick={handleScrollToTop}
        className="bg-primary text-primary-foreground hover:bg-primary/90 absolute right-4 bottom-4 z-10 size-12 rounded-full shadow-lg"
        aria-label="Scroll to top"
      >
        <ArrowUpToLine className="size-5" />
      </Button>
    </div>
  );
};
